import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngxs/store';
import {
  addDays,
  getISODay,
  hoursToMinutes,
  isSameDay,
  parseISO
} from 'date-fns';
import _ from 'lodash';
import { Subscription, skip } from 'rxjs';

import { ReactiveComponent } from 'src/app/components/shared/reactive-component';
import { MealConstant } from 'src/app/constants';
import { DateHelper, MealHelper } from 'src/app/helpers';
import {
  MealDeliveryAreaTiming,
  TakeoutOrderSetup,
  MealTime,
  NormalizedTicket,
  DeliveryType
} from 'src/app/models';
import { LanguageService } from 'src/app/services/language.service';
import { LocationSelectors } from 'src/app/store/location/location.selectors';
import { LoadMdaTimings } from 'src/app/store/takeout-delivery/takeout-delivery.action';
import { MdaTimingsByDate } from 'src/app/store/takeout-delivery/takeout-delivery.state';
import { TakeoutDeliverySelector } from 'src/app/store/takeout-delivery/takeout-delivery.state.selector';

export interface IOrderSetupModal {
  ticket?: NormalizedTicket;
}

export interface OrderSetupModalData {
  ticket_date: string;
  delivery_type: DeliveryType;
  meal_id: number;
  meal_delivery_area_timing_id?: number;
}

@Component({
  selector: 'pos-order-setup-modal',
  templateUrl: './order-setup.component.html',
  styleUrls: ['./order-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderSetupModal extends ReactiveComponent {
  @Input() ticket?: NormalizedTicket;

  readonly faTimes = faTimes;
  dates: string[] = [];
  form: FormGroup;

  selectedTimings: MealDeliveryAreaTiming[] = [];
  isCurrentDate = true;
  readonly currentLocale$ = this.languageService.getCurrentLocale$;
  readonly BREAKFAST_ID = MealConstant.BREAKFAST_ID;
  private allTimings: MdaTimingsByDate;

  get orderTypeValue() {
    return this.form.get('orderType').value;
  }

  private get dateControl() {
    return this.form.get('date');
  }

  private get dateValue(): string {
    return this.dateControl.value;
  }

  private get mealIdControl() {
    return this.form.get('mealId');
  }

  private get mealIdValue(): number {
    return this.mealIdControl.value;
  }

  currentMeal: number; // 1, 3, 5
  private currentMealToday: number; // 1, 3, 5

  constructor(
    private readonly fb: FormBuilder,
    private readonly modalController: ModalController,
    private readonly store: Store,
    private readonly cdr: ChangeDetectorRef,
    private readonly languageService: LanguageService
  ) {
    super();
  }

  *onSubscribe(): Iterable<Subscription> {
    this.init();

    yield this.store
      .select(TakeoutDeliverySelector.mealDeliveryAreaTimingsByDate)
      .pipe(skip(1))
      .subscribe((timingsByDateMeal) =>
        this.initDataAfterTimingsLoaded(timingsByDateMeal)
      );

    yield this.dateControl.valueChanges.subscribe((date) =>
      this.dateChangedHandler(date)
    );

    yield this.mealIdControl.valueChanges.subscribe(() => this.updateTimings());
  }

  // #region Private methods

  private dateChangedHandler(date: string) {
    const todayDateStr = DateHelper.format(new Date());
    this.isCurrentDate = date === todayDateStr;

    if (this.isCurrentDate) {
      this.currentMeal = this.currentMealToday;
      this.mealIdControl.setValue(this.currentMeal);
    } else {
      this.currentMeal = MealConstant.BREAKFAST_ID;
      this.updateTimings();
    }
  }

  private initDataAfterTimingsLoaded(timingsByDateMeal: MdaTimingsByDate) {
    this.allTimings = timingsByDateMeal;
    this.updateTimings();
  }

  private init() {
    const location = this.store.selectSnapshot(LocationSelectors.getLocation);
    const maxDaysInAdvance = this.store.selectSnapshot(
      LocationSelectors.getMaxDaysInAdvanceOrdering
    );
    const now = new Date();
    const todayDateStr = DateHelper.format(now);

    // When maxDaysInAdvance is 0, only show today
    if (maxDaysInAdvance === 0) {
      this.dates = [todayDateStr];
    } else {
      // Generate dates from today up to maxDaysInAdvance
      this.dates = [];
      for (let i = 0; i <= maxDaysInAdvance; i++) {
        this.dates.push(DateHelper.format(addDays(now, i)));
      }
    }
    this.form = this.fb.group({
      date: [this.dates[0], Validators.required],
      mealId: [null, Validators.required],
      orderType: [null, Validators.required],
      mealDeliveryAreaTiming: [null, Validators.required]
    });

    this.currentMealToday = MealHelper.getMealIdWithCutoffTime(
      location.meal_end_times
    );
    const ticketDate = this.ticket?.ticket_date;

    if (this.currentMealToday) {
      const isNilOrTodayTicketDate = !ticketDate || ticketDate === todayDateStr;
      if (isNilOrTodayTicketDate) {
        this.currentMeal = this.currentMealToday;
      } else {
        this.currentMeal = MealTime.Breakfast;
        this.isCurrentDate = false;
      }
    } else {
      this.dates.shift();
      this.currentMeal = MealTime.Breakfast;
      this.isCurrentDate = false;
    }

    this.store.dispatch(new LoadMdaTimings(this.dates));
    if (this.ticket) {
      this.form.patchValue({
        date: this.ticket.ticket_date,
        mealId: this.ticket.meal_id,
        orderType: this.ticket.delivery_type,
        mealDeliveryAreaTiming: this.ticket.meal_delivery_area_timing_id
      });
    } else {
      this.form.patchValue({
        date: _.first(this.dates),
        mealId: this.currentMeal
      });
    }
  }

  private updateTimings() {
    if (!this.allTimings || !this.dateValue || !this.mealIdValue) {
      return;
    }

    this.selectedTimings = this.getFilteredTimings(
      this.allTimings[this.dateValue][this.mealIdValue],
      getISODay(DateHelper.parse(this.dateValue)),
      this.mealIdValue
    );

    const ticketTimingId = this.ticket?.meal_delivery_area_timing_id;
    const ticketTimingIdx = _.findIndex(
      this.selectedTimings,
      ({ id }) => id === ticketTimingId
    );

    this.form.controls.mealDeliveryAreaTiming.setValue(
      ticketTimingIdx === -1 ? null : ticketTimingId
    );

    this.cdr.markForCheck();
  }

  /**
   * Return an array filtered timings based on mealId and or day of the week
   * @param timings Meal delivery area timings
   * @param mealId Meal Id
   * @param day Day of the week
   * @returns Filtered meal delivery area timing array
   */
  private getFilteredTimings(
    timings: MealDeliveryAreaTiming[],
    day: number,
    mealId?: number
  ): MealDeliveryAreaTiming[] {
    const today = new Date();
    const selectedDate = parseISO(this.dateValue);
    const isToday = isSameDay(today, selectedDate);
    const currentMinutesTime = this.getMinutes(
      new Date().getHours(),
      new Date().getMinutes()
    );
    const expTimings: MealDeliveryAreaTiming[] = [];
    const sortedTimings = _.sortBy(
      _.filter(timings, (t) => {
        const isOver =
          isToday &&
          this.getMinutes(t.from_hr, t.from_min) < currentMinutesTime;
        const isInvalid = t.exception || isOver || t.max_served;

        if (isInvalid) {
          if (t.exception) {
            expTimings.push(t);
          }
          return false;
        }

        return mealId ? t.meal_id === mealId && t.day === day : t.day === day;
      }),
      ['from_hr', 'from_min', 'to_hr', 'to_min']
    );

    return sortedTimings.filter((timing) => {
      if (_.isEmpty(expTimings)) {
        return true;
      }

      const timeOverlapIdx = this.timeOverlapIdx(timing, expTimings);

      if (timeOverlapIdx === -1) {
        return true;
      }

      expTimings.splice(timeOverlapIdx, 1);
      return false;
    });
  }

  private timeOverlapIdx(
    timing: MealDeliveryAreaTiming,
    expTimings: MealDeliveryAreaTiming[]
  ): number {
    const timingFrom = this.getMinutes(timing.from_hr, timing.from_min);
    const timingTo = this.getMinutes(timing.to_hr, timing.to_min);

    const overlapIdx = expTimings.findIndex((expTiming) => {
      const expTimingFrom = this.getMinutes(
        expTiming.from_hr,
        expTiming.from_min
      );
      const expTimingTo = this.getMinutes(expTiming.to_hr, expTiming.to_min);

      return (
        (timingFrom >= expTimingFrom && timingFrom <= expTimingTo) ||
        (timingTo >= expTimingFrom && timingTo <= expTimingTo)
      );
    });
    return overlapIdx;
  }

  private getMinutes(hours: number, min: number): number {
    return _.sum([hoursToMinutes(hours), min]);
  }

  // #endregion

  // #region Public methods
  onClose() {
    this.modalController.getTop().then((modal) => modal?.dismiss());
  }

  onSubmit(): void {
    const {
      date,
      orderType: delivery_type,
      mealDeliveryAreaTiming: meal_delivery_area_timing_id,
      mealId: meal_id
    } = this.form.value as TakeoutOrderSetup;

    this.modalController.dismiss(<OrderSetupModalData>{
      ticket_date: date,
      delivery_type,
      meal_delivery_area_timing_id,
      meal_id
    });
  }
  // #endregion
}
